import { isNumber } from '@bygghemmait/utilities';
import { useDebounce } from 'hooks';
import { useEffect } from 'react';

interface SliderValue {
  min: number;
  max: number;
}

interface EdgeValues {
  minValue: number;
  maxValue: number;
}

interface UseInputChangeProps extends EdgeValues {
  onChangeComplete: (sliderValue: SliderValue) => void;
  setLocalValue: (sliderValue: SliderValue) => void;
}

interface ShouldFilterProps extends SliderValue, EdgeValues {}

const shouldFilter = ({ min, max, minValue, maxValue }: ShouldFilterProps): boolean => {
  const hasValues = isNumber(min) && isNumber(max); // checks if they are numbers, this allows min to be 0. Was preventing filtering in such cases.
  if (!hasValues) {
    return !!hasValues;
  }

  const hasValidMinValue = min >= minValue && min <= max;
  const hasValidMaxValue = max <= maxValue && max >= min;
  return hasValidMaxValue && hasValidMinValue;
};

export const useInputChange = ({
  onChangeComplete,
  setLocalValue,
  minValue,
  maxValue
}: UseInputChangeProps) => {
  const WAIT_TIME = 500;
  const [changeComplete, cancelRequest] = useDebounce(onChangeComplete, WAIT_TIME);
  useEffect(() => cancelRequest, [cancelRequest]);

  return ({ min, max }: SliderValue) => {
    setLocalValue({ min, max });
    if (shouldFilter({ max, maxValue, min, minValue })) {
      changeComplete({ min, max });
    }
  };
};
